import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { photoSrc, sortedPhotoKeys } from 'modules/gallery';
import {
	deleteAlbum,
	deletePhoto,
	updateAlbum,
	uploadPhoto,
} from 'modules/web/admin';
import runQueue from 'modules/utils/queue';
import AlbumFields, { fieldsFromAlbum } from './AlbumFields';
import { Button, ConfirmButton } from './ui';

const ACCEPTED = ['image/jpeg', 'image/png'];

const statusText = (status) => {
	switch (status.state) {
		case 'queued':
			return 'Waiting…';
		case 'uploading':
			return `Uploading ${Math.round((status.progress || 0) * 100)}%`;
		case 'processing':
			return 'Processing…';
		case 'done':
			return 'Done';
		case 'error':
			return status.error;
		default:
			return '';
	}
};

const Uploader = ({ albumId, onUploaded, onError }) => {
	const [uploads, setUploads] = useState([]);
	const [dragging, setDragging] = useState(false);
	const inputRef = useRef();
	const nextId = useRef(0);

	const update = (id, status) =>
		setUploads((list) =>
			list.map((u) => (u.id === id ? { ...u, ...status } : u))
		);

	const start = async (files) => {
		const items = [...files].map((file) => ({
			id: nextId.current++,
			file,
			name: file.name,
			...(ACCEPTED.includes(file.type)
				? { state: 'queued' }
				: { state: 'error', error: 'Only JPEG and PNG files' }),
		}));

		setUploads((list) => [...list, ...items]);

		const valid = items.filter((item) => item.state === 'queued');

		await runQueue(valid, async (item) => {
			try {
				await uploadPhoto(albumId, item.file, (status) =>
					update(item.id, status)
				);
				update(item.id, { state: 'done' });
				onUploaded();
			} catch (err) {
				update(item.id, { state: 'error', error: err.message });
				onError(err);
			}
		});
	};

	const retry = (item) => {
		setUploads((list) => list.filter((u) => u.id !== item.id));
		start([item.file]);
	};

	const active = uploads.some((u) =>
		['queued', 'uploading', 'processing'].includes(u.state)
	);

	return (
		<div className='flex flex-col gap-3'>
			<div
				onDragOver={(e) => {
					e.preventDefault();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragging(false);
					start(e.dataTransfer.files);
				}}
				onClick={() => inputRef.current.click()}
				className={`border-2 border-dashed border-borderContent p-8 text-center cursor-pointer transition-opacity ${
					dragging ? 'opacity-100' : 'opacity-60 hover:opacity-100'
				}`}>
				Drop photos here or click to choose (JPEG or PNG)
				<input
					ref={inputRef}
					type='file'
					accept={ACCEPTED.join(',')}
					multiple
					hidden
					onChange={(e) => {
						start(e.target.files);
						e.target.value = '';
					}}
				/>
			</div>
			{uploads.length > 0 && (
				<div className='flex flex-col gap-1 text-sm'>
					{uploads.map((item) => (
						<div
							key={item.id}
							className='flex flex-row gap-3 place-items-center'>
							<span className='flex-1 truncate'>{item.name}</span>
							<span
								className={
									item.state === 'error' ? 'text-red-500' : 'opacity-70'
								}>
								{statusText(item)}
							</span>
							{item.state === 'error' && item.file && (
								<button
									type='button'
									className='underline'
									onClick={() => retry(item)}>
									Retry
								</button>
							)}
						</div>
					))}
					{!active && (
						<button
							type='button'
							className='self-start underline opacity-70'
							onClick={() => setUploads([])}>
							Clear list
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default ({ albumId, album, onChanged, onDeleted, onError }) => {
	const [fields, setFields] = useState(fieldsFromAlbum(album));
	const [saving, setSaving] = useState(false);
	const [busyPhoto, setBusyPhoto] = useState(null);

	const saved = fieldsFromAlbum(album);
	const dirty = JSON.stringify(fields) !== JSON.stringify(saved);
	const keys = sortedPhotoKeys(album);

	const run = async (action) => {
		try {
			await action();
			await onChanged();
		} catch (err) {
			onError(err);
		}
	};

	const save = async () => {
		setSaving(true);
		await run(() => updateAlbum(albumId, fields));
		setSaving(false);
	};

	const photoAction = async (key, action) => {
		setBusyPhoto(key);
		await run(action);
		setBusyPhoto(null);
	};

	return (
		<div className='flex flex-col gap-8'>
			<section className='flex flex-col gap-4'>
				<AlbumFields fields={fields} onChange={setFields} />
				<div className='flex flex-row gap-2 place-items-center'>
					<Button
						onClick={save}
						disabled={!dirty || saving || !fields.name.trim()}>
						{saving ? 'Saving…' : 'Save changes'}
					</Button>
					{dirty && (
						<Button variant='outline' onClick={() => setFields(saved)}>
							Discard
						</Button>
					)}
					<Link
						to={`/photo-collection/${albumId}`}
						className='text-sm underline opacity-70 ml-auto'>
						View on site
					</Link>
				</div>
				<span className='text-xs opacity-60'>
					Changes show on the public site within about a minute.
				</span>
			</section>

			<section className='flex flex-col gap-4'>
				<span className='text-lg'>Add photos</span>
				<Uploader
					albumId={albumId}
					onUploaded={onChanged}
					onError={onError}
				/>
			</section>

			<section className='flex flex-col gap-4'>
				<span className='text-lg'>
					Photos ({keys.length})
					{keys.length === 0 && (
						<span className='text-sm opacity-60'>
							{' '}
							· this album is hidden until it has photos
						</span>
					)}
				</span>
				<div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
					{keys.map((key) => {
						const photo = album.photos[key];
						const isCover = album.coverImage === key;

						return (
							<div
								key={key}
								className={`flex flex-col gap-2 ${
									busyPhoto === key ? 'opacity-40' : ''
								}`}>
								<div className='relative w-full aspect-[4/3] bg-[rgba(127,127,127,0.2)] overflow-hidden'>
									<img
										src={photoSrc(albumId, 'lowres', key, photo)}
										alt={photo.name || key}
										loading='lazy'
										className='w-full h-full object-contain'
									/>
									{isCover && (
										<span className='absolute top-1 left-1 bg-fill text-background text-xs px-2 py-1'>
											Cover
										</span>
									)}
								</div>
								<span className='text-xs truncate opacity-70'>
									{photo.name || key}
								</span>
								<div className='flex flex-row gap-2'>
									{!isCover && (
										<Button
											variant='outline'
											className='flex-1 !px-2 !py-1 text-xs'
											disabled={busyPhoto !== null}
											onClick={() =>
												photoAction(key, () =>
													updateAlbum(albumId, {
														coverImage: key,
													})
												)
											}>
											Make cover
										</Button>
									)}
									<ConfirmButton
										className='flex-1 !px-2 !py-1 text-xs'
										disabled={busyPhoto !== null}
										confirmText='Confirm'
										onConfirm={() =>
											photoAction(key, () =>
												deletePhoto(albumId, key)
											)
										}>
										Delete
									</ConfirmButton>
								</div>
							</div>
						);
					})}
				</div>
			</section>

			<section className='flex flex-col gap-2 border-t border-borderContent pt-6'>
				<span className='text-lg'>Danger zone</span>
				<div>
					<ConfirmButton
						confirmText={`Delete "${album.name}" and all ${keys.length} photos?`}
						onConfirm={async () => {
							try {
								await deleteAlbum(albumId);
								onDeleted();
							} catch (err) {
								onError(err);
							}
						}}>
						Delete album
					</ConfirmButton>
				</div>
			</section>
		</div>
	);
};
